import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useUserStore from '../store/userStore';
import { refreshAuth, hasSessionToken } from '../utils/api';

/**
 * OnboardingRoute - Wrapper for onboarding pages
 * 
 * REQUIRES authentication - user must be logged in first.
 * Flow: Auth → Onboarding → Home
 * 
 * If user has already completed onboarding, redirect to home.
 * If user is not authenticated, redirect to login.
 * 
 * State machine:
 * - loading: Initial state, checking auth
 * - allowed: User authenticated but onboarding not complete - allow access
 * - redirect-home: User already completed onboarding
 * - redirect-login: User not authenticated
 */
// Status known WITHOUT any request (same rules as the effect below); 'loading'
// only while a token exists but no user is hydrated (/auth/me recovery).
function initialStatus(user) {
  if (window.location.hash?.includes('session_id=') ||
      window.location.search?.includes('session_id=') ||
      window.location.search?.includes('magic_token=')) {
    return 'allowed';
  }
  if (user) return user.onboarding_complete ? 'redirect-home' : 'allowed';
  return hasSessionToken() ? 'loading' : 'redirect-login';
}

const OnboardingRoute = ({ children }) => {
  const navigate = useNavigate();
  const { user, setUser, setProfile } = useUserStore();
  const [status, setStatus] = useState(() => initialStatus(user));

  useEffect(() => {
    const checkAuth = async () => {
      // Skip auth check if we're in the middle of OAuth callback
      if (window.location.hash?.includes('session_id=') || 
          window.location.search?.includes('session_id=') ||
          window.location.search?.includes('magic_token=')) {
        setStatus('allowed');
        return;
      }

      const tokenExists = hasSessionToken();
      
      console.log('[OnboardingRoute] Checking auth:', {
        tokenExists,
        userInStore: !!user,
        onboardingComplete: user?.onboarding_complete
      });

      // Case 1: User in store with completed onboarding - redirect to home
      if (user && user.onboarding_complete) {
        console.log('[OnboardingRoute] User has completed onboarding, redirecting to /home');
        setStatus('redirect-home');
        return;
      }

      // Case 2: User in store but onboarding not complete - allow access
      if (user && !user.onboarding_complete) {
        console.log('[OnboardingRoute] User authenticated, onboarding not complete - allowing access');
        setStatus('allowed');
        return;
      }

      // Case 3: No token - redirect to login
      if (!tokenExists) {
        console.log('[OnboardingRoute] No token, redirecting to /login');
        setStatus('redirect-login');
        return;
      }

      // Case 4: Token exists but no user in store - refresh from API
      console.log('[OnboardingRoute] Token exists, refreshing auth...');
      try {
        const { user: fetchedUser, profile } = await refreshAuth();
        
        // Update store
        setUser(fetchedUser);
        if (profile) setProfile(profile);
        
        // Check onboarding status
        if (fetchedUser.onboarding_complete) {
          console.log('[OnboardingRoute] Auth refreshed - user completed onboarding, redirecting to /home');
          setStatus('redirect-home');
        } else {
          console.log('[OnboardingRoute] Auth refreshed - user needs onboarding, allowing access');
          setStatus('allowed');
        }
      } catch (error) {
        console.log('[OnboardingRoute] Auth refresh failed:', error.message);
        setStatus('redirect-login');
      }
    };

    checkAuth();
  }, [user, setUser, setProfile]);

  // Handle redirects based on status
  useEffect(() => {
    if (status === 'redirect-home') {
      navigate('/home', { replace: true });
    } else if (status === 'redirect-login') {
      navigate('/login', { replace: true });
    }
  }, [status, navigate]);

  // Loading state
  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-accent border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Only render children when allowed
  if (status !== 'allowed') {
    return null;
  }

  return children;
};

export default OnboardingRoute;
